import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, CreditCard as CardIcon, Trash2, Scissors, CalendarClock } from "lucide-react";

const empty = { bank: "", card_name: "", statement_day: 1, due_day: 10, current_debt: "", min_payment: "", limit: "", used: "", color: "#3B82F6" };
const colors = ["#3B82F6", "#22C55E", "#E11D48", "#7C3AED", "#F59E0B", "#06B6D4"];

// Next occurrence of a day-of-month, days remaining
function daysToNext(day) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let d = new Date(today.getFullYear(), today.getMonth(), day);
  if (d < today) d = new Date(today.getFullYear(), today.getMonth() + 1, day);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}
function nextLabel(day) {
  const n = daysToNext(day);
  if (n === 0) return "Bugün";
  if (n === 1) return "Yarın";
  return `${n} gün kaldı`;
}

export default function Cards() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const load = () => api.get("/cards").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleTransactions = async (id) => {
    if (selectedCardId === id) {
      setSelectedCardId(null);
      setTransactions([]);
      return;
    }
    setSelectedCardId(id);
    setTransactions([]);
    try {
      const r = await api.get(`/cards/${id}/transactions`);
      setTransactions(r.data.slice(0, 10));
    } catch (e) {
      toast.error("Harcamalar yüklenemedi");
    }
  };

  const save = async () => {
    if (!form.bank || !form.card_name) { toast.error("Banka ve kart adı zorunlu"); return; }
    const payload = {
      bank: form.bank, card_name: form.card_name,
      statement_day: parseInt(form.statement_day) || 1, due_day: parseInt(form.due_day) || 10,
      current_debt: parseFloat(form.current_debt) || 0, min_payment: parseFloat(form.min_payment) || 0,
      limit: parseFloat(form.limit) || 0, used: parseFloat(form.used) || 0, color: form.color,
    };
    try {
      if (editId) await api.put(`/cards/${editId}`, payload);
      else await api.post("/cards", payload);
      toast.success(editId ? "Kart güncellendi" : "Kart eklendi");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  const del = async (id) => { await api.delete(`/cards/${id}`); toast.success("Silindi"); load(); };
  const openEdit = (c) => { setEditId(c.id); setForm({ ...c }); setOpen(true); };

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <PageHeader
        testid="cards-header"
        title="Ticari Kredi Kartları"
        subtitle="Hangi kart ne zaman hesap kesiyor, son ödeme ne zaman — hepsi tek ekranda."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-credit-card" className="px-4 py-2.5 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] transition-colors font-semibold text-sm flex items-center gap-2 shrink-0">
                <Plus className="w-4 h-4" /> Kart Ekle
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#161D2A] border-[#253043] text-[#F8FAFC]">
              <DialogHeader><DialogTitle className="font-display">{editId ? "Kartı Düzenle" : "Yeni Kredi Kartı"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Banka"><input data-testid="card-bank-input" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className={inp} placeholder="Garanti BBVA" /></Field>
                <Field label="Kart Adı"><input data-testid="card-name-input" value={form.card_name} onChange={(e) => setForm({ ...form, card_name: e.target.value })} className={inp} placeholder="Bonus Business" /></Field>
                <Field label="Hesap Kesim Günü"><input type="number" min="1" max="31" value={form.statement_day} onChange={(e) => setForm({ ...form, statement_day: e.target.value })} className={inp} /></Field>
                <Field label="Son Ödeme Günü"><input data-testid="card-due-day-input" type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} className={inp} /></Field>
                <Field label="Dönem Borcu (₺)"><input data-testid="card-debt-input" type="number" value={form.current_debt} onChange={(e) => setForm({ ...form, current_debt: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Asgari Tutar (₺)"><input type="number" value={form.min_payment} onChange={(e) => setForm({ ...form, min_payment: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Limit (₺)"><input type="number" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Kullanılan (₺)"><input type="number" value={form.used} onChange={(e) => setForm({ ...form, used: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Renk" full>
                  <div className="flex gap-2">
                    {colors.map((c) => (
                      <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-8 h-8 rounded-lg transition-transform ${form.color === c ? "ring-2 ring-white scale-110" : ""}`} style={{ background: c }} />
                    ))}
                  </div>
                </Field>
              </div>
              <DialogFooter>
                <button data-testid="card-save-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] transition-colors font-semibold text-sm w-full">{editId ? "Güncelle" : "Kaydet"}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.length === 0 && <div className="card-surface p-10 text-center text-[#94A3B8] text-sm md:col-span-2">Henüz kart eklenmedi.</div>}
        {items.map((c) => {
          const usePct = c.limit > 0 ? Math.min(100, (c.used / c.limit) * 100) : 0;
          const dueDays = daysToNext(c.due_day);
          return (
            <div key={c.id} data-testid="credit-card-item" className="card-surface p-6 fade-up relative overflow-hidden">
              <div className="absolute right-0 top-0 h-full w-1.5" style={{ background: c.color }} />
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${c.color}22` }}>
                    <CardIcon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <div>
                    <button onClick={() => openEdit(c)} className="font-display font-semibold text-left">{c.bank}</button>
                    <div className="text-xs text-[#94A3B8]">{c.card_name}</div>
                  </div>
                </div>
                <button onClick={() => del(c.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3 rounded-xl bg-[#111620]">
                  <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mb-1"><Scissors className="w-3 h-3" /> Hesap Kesim</div>
                  <div className="font-mono-num font-bold">Her ayın {c.statement_day}.</div>
                </div>
                <div className="p-3 rounded-xl bg-[#111620]">
                  <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mb-1"><CalendarClock className="w-3 h-3" /> Son Ödeme</div>
                  <div className="font-mono-num font-bold">Her ayın {c.due_day}.</div>
                  <div className={`text-[11px] mt-0.5 ${dueDays <= 3 ? "text-[#F59E0B]" : "text-[#94A3B8]"}`}>{nextLabel(c.due_day)}</div>
                </div>
              </div>

              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#94A3B8]">Dönem Borcu</span>
                <span className="font-mono-num font-bold">{formatTRY(c.current_debt)}</span>
              </div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-[#94A3B8]">Asgari Tutar</span>
                <span className="font-mono-num text-[#F59E0B]">{formatTRY(c.min_payment)}</span>
              </div>

              <div className="flex justify-between text-[11px] text-[#94A3B8] mb-1.5">
                <span>Limit Kullanımı</span>
                <span>{usePct.toFixed(0)}% · {formatTRY(c.used)} / {formatTRY(c.limit)}</span>
              </div>
              <div className="h-2 rounded-full bg-[#111620] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${usePct}%`, background: usePct > 85 ? "#EF4444" : c.color }} />
              </div>

              <div className="border-t border-[#253043] pt-4 mt-4">
                <button 
                  onClick={() => toggleTransactions(c.id)} 
                  className="w-full py-1.5 text-sm font-semibold text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg transition-colors"
                >
                  {selectedCardId === c.id ? "İşlemleri Gizle" : "Son İşlemleri Göster"}
                </button>
                
                {selectedCardId === c.id && (
                  <div className="mt-4 space-y-2">
                    {transactions.length === 0 ? (
                      <div className="text-center text-sm text-[#94A3B8] py-2">Henüz kart işlemi bulunmuyor.</div>
                    ) : (
                      transactions.map((t, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[#111620]">
                          <div>
                            <div className="text-sm text-[#F8FAFC]">{t.description}</div>
                            <div className="text-[11px] text-[#94A3B8]">{t.transaction_date}</div>
                          </div>
                          <div className="text-sm font-mono-num text-[#F8FAFC]">
                            {formatTRY(t.amount)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#3B82F6] outline-none transition-colors text-sm";
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-xs text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
